import React, { useEffect, useRef, useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import WorkflowCard from './WorkflowCard';

const Swimlane = ({ 
  lane, 
  cardRefs,
  hoveredLinkedCardId,
  onCardHover,
  highlightedCardId,
  swimlaneData,
  hoveredChipTargetCardId,
  setHoveredChipTargetCardId,
  highlightedDependency,
  setHighlightedDependency
}) => {
  const containerRef = useRef(null);
  
  // Calculate dependency levels and group cards
  const { cardsByLevel, maxLevel } = useMemo(() => {
    const groupedCards = {};
    
    // Process each card to determine its level
    lane.cards.forEach(card => {
      let dependencyLevel = 0;
      
      if (card.dependencies && card.dependencies.length > 0) {
        // For each dependency, find its position in the workflow
        const dependencyLevels = card.dependencies.map(depTitle => {
          // Find the dependency card across all swimlanes
          for (const swimlane of swimlaneData) {
            const depCard = swimlane.cards.find(c => 
              c.title === depTitle || c.id === depTitle.toLowerCase()
            );
            if (depCard) {
              // If found, return its level + 1 (this card should be one level higher)
              return depCard.gridColumn || 1;
            }
          }
          return 0; // If dependency not found, default to 0
        });
        
        // This card's level should be one more than its highest dependency
        dependencyLevel = Math.max(0, ...dependencyLevels);
      } else {
        // If no dependencies, use the card's gridColumn if available, or default to 0
        dependencyLevel = card.gridColumn ? card.gridColumn - 1 : 0;
      }
      
      // Add card to its level group
      if (!groupedCards[dependencyLevel]) {
        groupedCards[dependencyLevel] = [];
      }
      groupedCards[dependencyLevel].push(card);
    });
    
    const levels = Object.keys(groupedCards).map(Number);
    const maxLevelValue = levels.length > 0 ? Math.max(...levels) : 0;
    
    return { cardsByLevel: groupedCards, maxLevel: maxLevelValue };
  }, [lane.cards, swimlaneData]);
  
  // Calculate total width needed for all levels with moderate spacing
  const totalWidth = maxLevel * 450 + 350; // 450px per level + extra space
  // Set container width to accommodate all cards
  useEffect(() => {
    if (containerRef.current && totalWidth > containerRef.current.clientWidth) {
      containerRef.current.style.minWidth = `${totalWidth}px`;
    }
  }, [totalWidth]);
  
  return (
    <Box 
      ref={containerRef}
      sx={{
        display: 'flex',
        flexDirection: 'row', // Changed to row to place title on left and cards on right
        backgroundColor: lane.backgroundColor || 'rgba(0, 0, 0, 0.02)',
        mb: 0, // Removed spacing between swimlanes
        borderRadius: 1,
        borderBottom: '1px solid rgba(0, 0, 0, 0.12)', // Added border between swimlanes
        p: 0, // Removed padding to control it in child elements
        width: '100%',
        overflow: 'hidden', // Hide overflow content
        // Removed minWidth to prevent stretching beyond container
      }}
    >
      {/* Left side title section */}
      <Box 
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center', // Center content vertically
          p: 1.5,
          width: '100px', // Fixed width for title section
          minHeight: '205px', // Slightly increased height
          borderRight: '1px solid rgba(0, 0, 0, 0.12)', // Border to the right of title section
          backgroundColor: 'rgba(0, 0, 0, 0.03)', // Slightly darker background for title area
        }}
      >
        {lane.icon && (
          <Box sx={{ mb: 0.5 }}>{lane.icon}</Box> // Reduced spacing from 1 to 0.5
        )}
        <Typography variant="subtitle1" sx={{ fontWeight: 'medium', fontSize: '0.95rem', textAlign: 'center' }}>
          {lane.title}
        </Typography>
      </Box>
      
      {/* Right side cards section */}
      <Box 
        sx={{
          position: 'relative',
          minHeight: 'auto', // Changed from fixed height to auto
          flexGrow: 1,
          width: 'calc(100% - 100px)', // Full width minus title section width
          overflowX: 'auto', // Enable horizontal scrolling
          overflowY: 'hidden', // Prevent vertical overflow
          p: '1.5rem 1.5rem 0.25rem 1.5rem', // Further reduced bottom padding
          ml: 2.5, // Increased margin to the left (right of the border)
          '&::-webkit-scrollbar': {
            height: 6,
          },
          '&::-webkit-scrollbar-track': {
            backgroundColor: 'rgba(0,0,0,0.03)',
            borderRadius: 10,
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: 'rgba(0,0,0,0.1)',
            borderRadius: 10,
          },
        }}
      >
        {/* Render cards by dependency level */}
        {Object.keys(cardsByLevel)
          .sort((a, b) => Number(a) - Number(b))
          .map(level => {
            const cardsInLevel = cardsByLevel[level];
            const levelPosition = Number(level) * 450; // 450px spacing between levels
            
            return (
              <Box 
                key={`level-${level}`}
                sx={{
                  display: 'flex',
                  flexDirection: 'column', // Stack cards vertically within their swimlane
                  position: 'absolute',
                  left: `${levelPosition}px`,
                  top: 0,
                  height: 'auto',
                  gap: 2, // Gap between cards in the same level
                  zIndex: 1, // Ensure cards appear above background but below hover effects
                  padding: '10px 0 0 0', // Added top padding to move cards down
                  width: '300px', // Fixed width for each column to ensure cards aren't cut off
                  maxHeight: 'none' // Allow unlimited height
                }}
              >
                {cardsInLevel.map(card => (
                  <Box
                    key={card.id}
                    sx={{ 
                      mb: 1, // Reduced margin bottom from 2 to 1
                      width: '100%' // Ensure card container takes full width
                    }}
                  >
                    <WorkflowCard 
                      card={card}
                      cardRef={cardRefs[card.id]}
                      onMouseEnter={onCardHover}
                      onMouseLeave={() => onCardHover(null)}
                      isHighlighted={highlightedCardId === card.id}
                      hoveredLinkedCardId={hoveredLinkedCardId}
                      swimlaneData={swimlaneData}
                      hoveredChipTargetCardId={hoveredChipTargetCardId}
                      setHoveredChipTargetCardId={setHoveredChipTargetCardId}
                      highlightedDependency={highlightedDependency}
                      setHighlightedDependency={setHighlightedDependency}
                    />
                  </Box>
                ))}
              </Box>
            );
          })}
      </Box>
    </Box>
  );
};

export default Swimlane;
